/*
  How the header's menus open. Two behaviours, one for each arrangement of the
  menu: the bar's dropdowns answer to the pointer as well as to a click, and the
  drawer's Products item opens where it stands instead of sliding in.
*/

/*
  The Products item in the phone drawer.

  Dawn's MenuDrawer binds a click handler to every summary in the drawer, which
  hides the menu behind the incoming panel and traps focus inside it. That is
  right for a submenu that slides in over the menu and wrong for one that expands
  in place: it would leave the rest of the menu unreachable behind a panel that is
  sitting in the middle of it.

  So the click is stopped before it reaches that handler. In the capture phase and
  on the document, which is what makes it independent of the order the scripts
  load in — a listener added later on the summary itself would still run second.
  Only propagation is stopped, never the default action, so the details element
  goes on opening and closing itself.
*/
document.addEventListener(
  'click',
  (event) => {
    if (event.target.closest?.('summary.products-drawer__toggle')) event.stopPropagation();
  },
  true
);

/*
  Stopping that click also keeps Dawn's general summary handler from updating
  aria-expanded. The details element remains the source of truth, so mirror its
  native toggle state instead of trying to predict what a click will do. Capture
  catches the non-bubbling toggle event and also covers keyboard or scripted
  changes to the accordion.
*/
document.addEventListener(
  'toggle',
  (event) => {
    if (!event.target.matches?.('details.products-drawer')) return;

    const summary = event.target.querySelector(':scope > summary');
    summary?.setAttribute('aria-expanded', String(event.target.open));
  },
  true
);

/*
  Opens the header's menus on hover as well as on click.

  The menus are <details> elements, which open on click and nothing else. On a
  pointer that can hover, a menu bar is expected to answer to the pointer passing
  over it, so that is added here — and only here, so the markup, the keyboard
  behaviour and the Escape handling stay as Dawn wrote them.

  Hovering and clicking say different things, and the difference is kept:

  - Passing over a menu opens it, and leaving closes it again after a moment.
    The delay is what lets the pointer travel diagonally from the link to the
    far side of the panel without cutting the corner and closing it.
  - Clicking pins the menu open. Clicking a menu that hover already opened
    therefore keeps it rather than closing it, which is what the click was for;
    clicking it a second time, or anywhere outside it, closes it.

  Pointers that cannot hover — every touchscreen, so every phone and tablet —
  are left with click alone. That is read at the moment of the event rather than
  at startup, so plugging a mouse into a tablet is enough to bring hover back.
  Below 990px the menu bar is not on the page at all, so no width is checked.
*/

(() => {
  const OPEN_DELAY = 60;
  /* Long enough to cross the gap between the link and the panel under it. */
  const CLOSE_DELAY = 200;

  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const menus = [];
  const menuInstances = new WeakMap();

  class HoverMenu {
    constructor(element) {
      this.element = element;
      this.details = element.querySelector('details');
      this.summary = this.details.querySelector('summary');
      this.pinned = false;
      this.timer = null;

      this.element.addEventListener('mouseenter', this.onEnter.bind(this));
      this.element.addEventListener('mouseleave', this.onLeave.bind(this));
      this.summary.addEventListener('click', this.onClick.bind(this));
      /* Closing can also come from Escape or from focus leaving, both of which
         Dawn already handles: whatever the cause, the pin goes with it. */
      this.details.addEventListener('toggle', () => {
        if (!this.details.open) this.pinned = false;
      });
    }

    get isOpen() {
      return this.details.open;
    }

    open() {
      clearTimeout(this.timer);
      if (this.isOpen) return;

      menus.forEach((menu) => {
        if (menu !== this) menu.close();
      });

      this.details.open = true;
      this.summary.setAttribute('aria-expanded', 'true');
    }

    close() {
      clearTimeout(this.timer);
      if (!this.isOpen) return;

      this.details.open = false;
      this.summary.setAttribute('aria-expanded', 'false');
      this.pinned = false;
    }

    onEnter() {
      if (!finePointer.matches) return;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.open(), OPEN_DELAY);
    }

    onLeave() {
      if (!finePointer.matches) return;
      clearTimeout(this.timer);
      if (this.pinned) return;
      this.timer = setTimeout(() => this.close(), CLOSE_DELAY);
    }

    onClick(event) {
      if (this.isOpen && !this.pinned) {
        /* Open because the pointer is over it. The click asks to keep it, so the
           toggle the browser would do is stopped and the menu is pinned instead. */
        event.preventDefault();
        this.pinned = true;
        return;
      }

      /* Otherwise the browser toggles it: a closed menu opens pinned, a pinned
         one closes. */
      this.pinned = !this.isOpen;
    }
  }

  /* Re-read on section load as well as at startup: the theme editor replaces the
     header's markup whenever a setting changes, and a merchant setting this menu
     up is doing exactly that. Existing elements keep their controller so an
     unrelated section reload cannot attach the same listeners a second time. */
  const collect = () => {
    const currentMenus = [];

    document.querySelectorAll('header-menu').forEach((element) => {
      if (!element.querySelector('details > summary')) return;

      let menu = menuInstances.get(element);

      if (!menu) {
        menu = new HoverMenu(element);
        menuInstances.set(element, menu);
      }

      currentMenus.push(menu);
    });

    menus.splice(0, menus.length, ...currentMenus);
  };

  collect();
  document.addEventListener('shopify:section:load', collect);

  /* A pinned menu is the only one still open once the pointer has left it, so it
     is the only one this has to close. */
  document.addEventListener('click', (event) => {
    menus.forEach((menu) => {
      if (menu.pinned && !menu.element.contains(event.target)) menu.close();
    });
  });
})();
